import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';


function SignUpForm() {
    const navigate = useNavigate();
    const [isTermsOpen, setIsTermsOpen] = useState(false);
    const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        birthDate: '',
        password: '',
        confirmPassword: '',
        termsAccepted: false,
    });
    const [errors, setErrors] = useState({});

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    const validatePassword = (password) => {
        return passwordRegex.test(password);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    const toggleConfirmPasswordVisibility = () => {
        setConfirmPasswordVisible(!confirmPasswordVisible);
    };
    const handleTermsClick = (e) => {
        e.preventDefault();
        setIsTermsOpen(true);
    };

    const handlePrivacyClick = (e) => {
        e.preventDefault();
        setIsPrivacyOpen(true);
    };

    const closeTermsModal = () => setIsTermsOpen(false);
    const closePrivacyModal = () => setIsPrivacyOpen(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { firstName, lastName, email, phone, birthDate, password, confirmPassword } = formData;

        // Validate required fields
        if (!firstName || !lastName || !email || !phone || !birthDate || !password || !confirmPassword) {
            toast.error('Please fill in all required fields.');
            return;
        }

        if (password !== confirmPassword) {
            setErrors({ confirmPassword: "Passwords do not match" });
            return;
        }

        const requestBody = {
            firstName,
            lastName,
            email,
            mobile: phone,
            Dob: birthDate,
            password,
        };

        console.log('Request:', requestBody); // Log the request body for debugging

        try {
            const response = await fetch(`https://api.itrain.io/student/createStudent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (response.ok) {
                const data = await response.json();
                toast.success('Form submitted successfully!');
                setTimeout(() => {
                    navigate('/payment');
                }, 3000);
            } else {
                const errorData = await response.json();
                toast.error('Failed to submit form. Please try again later.');
                console.error('Error Data:', errorData); // Log error data for debugging
            }
        } catch (error) {
            console.error('Network Error:', error);
        }
    };
    const termsData = [
        {
            title: "Acceptance of Terms",
            content:
                "By accessing, browsing, or using our services, you acknowledge that you have read, understood, and agree to comply with these terms. If you do not agree to these terms, you should not use our services.",
        },
        {
            title: "Changes to Terms",
            content:
                "We reserve the right to update or modify these terms at any time without prior notice. Your continued use of the services after changes are made constitutes your acceptance of the new terms. We encourage you to review the terms periodically.",
        },
        {
            title: "Use of Services",
            content: [
                "You agree to use our Services only for lawful purposes and in accordance with these terms.",
                "You will not engage in any activity that interferes with or disrupts the Services.",
                "Unauthorized access to our systems is prohibited.",
            ],
        },
        {
            title: "Training Courses Enrollment and Payments",
            content: [
                "Enrollment in a course is confirmed upon receipt of full payment of $199 USD monthly.",
                "Auto renewal of payments will be approved by the client upon sign-up.",
            ],
        },
        {
            title: "User Accounts",
            content: [
                "To access certain features, you may need to create an account.",
                "You are responsible for maintaining the confidentiality of your account information.",
            ],
        },
        {
            title: "Intellectual Property",
            content:
                "All content, training materials, trademarks, logos, and intellectual property provided through our Services remain the property of TITA Technologies Inc.",
        },
        {
            title: "Privacy Policy",
            content: "Your use of our Services is also governed by our Privacy Policy.",
        },
        {
            title: "Limitation of Liability",
            content:
                "We provide the Services 'as is' without warranties of any kind, either express or implied.",
        },
        {
            title: "Refund Policy",
            content:
                "Clients can cancel the subscription before the start of the next month's billing cycle. There is no contract on the subscription model, and a refund can be initiated if fewer than 3 students enroll in a batch.",
        },
        {
            title: "Termination",
            content:
                "We reserve the right to suspend or terminate your access to the Services at our discretion.",
        },
        {
            title: "Governing Law",
            content: "These Terms are governed by and construed in accordance with the laws of India.",
        },
    ];

    const TermsSection = ({ index, title, content }) => (
        <div>
            <h3 style={{ fontWeight: "bold" }}>{`${index + 1}. ${title}`}</h3>
            {Array.isArray(content) ? (
                <ul style={{ paddingLeft: "1.5rem", listStyleType: "disc" }}>
                    {content.map((item, idx) => (
                        <li key={idx} style={{ marginBottom: "0.8rem" }}>
                            {item}
                        </li>
                    ))}
                </ul>
            ) : (
                <p>{content}</p>
            )}
        </div>
    );

    const privacySections = [
        {
            content: "Effective Date: December 15, 2024"
        },
        {
            content: (
                <>
                    TITA Technologies Inc. ("we," "our," or "us") is committed to protecting your privacy.
                    This Privacy Policy describes how we collect, use, disclose, and safeguard your personal information
                    when you use or interact with us regarding our services, including our website, applications,
                    <strong>iTrain</strong> (training platform), <strong>iCertify</strong> (certificates and badges), and any other IT solutions.
                    By using our services, you agree to the terms of this Privacy Policy.
                </>
            )
        },
        {
            title: "1. Information We Collect",
            description: "We may collect the following types of information:",
            content: [
                { label: "Personal Information", description: "Name, email address, phone number, billing/shipping address, payment information (e.g., debit card, credit card, PayPal, bank account details)." },
                { label: "Technical Information", description: "IP address, browser type/version, operating system, device information, log files, and usage data." },
                { label: "Other Information", description: "Communication preferences, feedback, reviews, and other information provided directly to us." }
            ]
        },
        {
            title: "2. How We Use Your Information",
            description: "We may use the information we collect for the following purposes:",
            content: [
                { label: "To provide services", description: "Process transactions, manage accounts, and deliver IT and training solutions." },
                { label: "To improve our services", description: "Analyze usage patterns and troubleshoot technical issues." },
                { label: "To communicate with you", description: "Respond to inquiries, send updates, and provide customer support." },
                { label: "To comply with legal obligations", description: "Prevent fraud, meet regulatory requirements, and protect our rights." },
                { label: "For marketing purposes", description: "Send promotional materials, newsletters, and special offers (with your consent)." }
            ]
        },
        {
            title: "3. How We Share Your Information",
            description: "We do not sell your personal information. However, we may share your information with:",
            content: [
                { label: "Service providers", description: "Third-party vendors assisting with payment processing, hosting, data storage, or analytics." },
                { label: "Business partners", description: "Collaborators providing complementary services." },
                { label: "Legal authorities", description: "When required by law or to protect our legal interests." },
                { label: "Successors", description: "In case of a merger, acquisition, or asset sale." }
            ]
        },
        {
            title: "4. Data Security",
            content: "We implement reasonable technical and organizational measures to safeguard your information. However, no method of transmission or storage is entirely secure, and we cannot guarantee absolute security."
        },
        {
            title: "5. Your Rights and Choices",
            description: "Depending on your location, you may have the following rights:",
            content: [
                { label: "Access and correction", description: "Request access to or correction of your personal information." },
                { label: "Data portability", description: "Obtain a copy of your information in a usable format." },
                { label: "Erasure", description: "Request deletion of your information (subject to legal obligations)." },
                { label: "Opt-out", description: "Withdraw consent for marketing communications or data processing." }
            ]
        },
        {
            content: (
                <>To exercise these rights, please contact us at <a href="mailto:contact@titatechnologies.com">contact@titatechnologies.com</a>.</>
            )
        },
        {
            title: "6. Cookies and Tracking Technologies",
            content: "We may use cookies, web beacons, and other tracking technologies to enhance your experience and analyze website usage. You can manage your cookie preferences through your browser settings."
        },
        {
            title: "7. Third-Party Links",
            content: "Our services may contain links to third-party websites and applications, including data centers like AWS, Google, or others. We encourage you to review their policies."
        },
        {
            title: "8. International Data Transfers",
            content: "If you are accessing our services from outside your home country, your information may be transferred to and processed in a country with different privacy laws. By using our services, you consent to such transfers."
        },
        {
            title: "9. Children’s Privacy",
            content: "Our services are not intended for children under 13 years of age. We do not knowingly collect information from children. If we become aware of such data, we will delete it promptly."
        },
        {
            title: "10. Updates to This Policy",
            content: "We may update this Privacy Policy periodically. Any changes will be effective upon posting the revised policy on our website. We encourage you to review this policy regularly."
        },
        {
            title: "11. Contact Us",
            description: "If you have any questions or concerns about this Privacy Policy, please contact us:",
            content: (
                <ul>
                    <li><strong>Email:</strong> <a href="mailto:contact@titatechnologies.com">contact@titatechnologies.com</a></li>
                    <li><strong>Phone:</strong> India - +91 86672 96757, US/Canada - +1-647-988-7328</li>
                    <li><strong>Address:</strong> No 81,Rajaram street, jawahar nagar,Thirumangalam-625706, India</li>
                </ul>
            )
        },
        {
            content: "By using our services, you acknowledge that you have read and understood this Privacy Policy.",
        }
    ];





    return (
        <>
            <ToastContainer />
            <div className="animated-background">
                <div className="gradient-sphere sphere-1" />
                <div className="gradient-sphere sphere-2" />
                <div className="gradient-sphere sphere-3" />
                <div className="particles" id="particles" />
            </div>
            <div className="form-container">
                <section className="form-section">
                    <header className="form-header">SignUp</header>

                    <form action="#" className="form" onSubmit={handleSubmit}>
                        <div className="form-row">
                            <div className="form-field">
                                <label>First Name</label>
                                <input type="text" name="firstName" placeholder="Enter first Name" required onChange={handleChange} />
                            </div>
                            <div className="form-field">
                                <label>Last Name</label>
                                <input type="text" name="lastName" placeholder="Enter Last Name" required onChange={handleChange} />
                            </div>
                        </div>

                        <div className="form-field">
                            <label>Email Address</label>
                            <input type="text" name="email" placeholder="Enter email address" required onChange={handleChange} />
                        </div>

                        <div className="form-row">
                            <div className="form-field">
                                <label>Phone Number</label>
                                <input type="number" name="phone" placeholder="Enter phone number" required onChange={handleChange} />
                            </div>
                            <div className="form-field">
                                <label>Birth Date</label>
                                <input type="date" name="birthDate" required onChange={handleChange} />
                            </div>
                        </div>


                        <div className="form-row">
                            <div className="form-field">
                                <label>Password</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={passwordVisible ? 'text' : 'password'}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder="Enter password"
                                    />
                                    <span
                                        onClick={() => setPasswordVisible(!passwordVisible)}
                                        style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                                    >
                                        <FontAwesomeIcon icon={passwordVisible ? faEyeSlash : faEye} />
                                    </span>
                                </div>
                                {errors.password && <span>{errors.password}</span>}
                            </div>
                            <div className="form-field">
                                <label>Confirm Password</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={confirmPasswordVisible ? 'text' : 'password'}
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        placeholder="Confirm password"
                                    />
                                    <span
                                        onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                                        style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                                    >
                                        <FontAwesomeIcon icon={confirmPasswordVisible ? faEyeSlash : faEye} />
                                    </span>
                                </div>
                                {errors.confirmPassword && <span>{errors.confirmPassword}</span>}
                            </div>
                        </div>

                        <div className="form-terms">
                            <div className="terms-checkbox">
                                <input
                                    type="checkbox"
                                    id="terms"
                                    name="termsAccepted"
                                    required
                                    onChange={handleChange}
                                />
                                <label htmlFor="terms">
                                    I accept the{' '}
                                    <a href="#" onClick={handleTermsClick} style={{ color: "#FF001E" }}><b>terms and conditions</b></a> and{' '}

                                    <a href="#" onClick={handlePrivacyClick} style={{ color: "#FF001E" }}><b>privacy policy</b></a>.
                                </label>
                            </div>
                            {errors.terms && <span className="error-text">{errors.terms}</span>}
                            <a href="http://itrain.io" target="_blank" rel="noopener noreferrer" className="login-link">
                                Already a user? Login here
                            </a>
                        </div>

                        <button type="submit" disabled={!formData.termsAccepted} className="submit-btn">Submit</button>
                    </form>
                </section>
            </div>

            {isTermsOpen && (
                <div
                    style={{ display: "flex", position: "fixed", zIndex: "1000", left: "0", top: "0", width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.4)", justifyContent: "center", alignItems: "center", }}>
                    <div
                        style={{ backgroundColor: "#fefefe", padding: "20px", border: "1px solid #888", width: "80%", maxWidth: "80%", height: "90vh", maxHeight: "90vh", overflowY: "auto", scrollBehavior: "smooth", lineHeight: "1.6", borderRadius: "8px", position: "relative", }}>
                        {/* Close button */}
                        <span
                            style={{ color: "#aaa", position: "absolute", top: "10px", right: "10px", fontSize: "28px", fontWeight: "bold", cursor: "pointer", }}
                            onClick={closeTermsModal}
                        >
                            &times;
                        </span>

                        <h2 style={{ marginBottom: "1rem" }}>Terms and Conditions</h2>
                        <p> <strong>Effective Date:</strong> 12th December 2024 </p>
                        <p> <strong>Last Updated:</strong> 10th December 2024 </p>
                        <p>
                            Welcome to TITA Technologies Inc., ("Company", "we", "our", "us").
                            By accessing or using our services, websites, training, or IT
                            products ("Services"), you agree to be bound by these Terms and
                            Conditions ("Terms"). Please read them carefully.
                        </p>

                        {termsData.map((section, index) => (
                            <TermsSection key={index} index={index} title={section.title} content={section.content} />
                        ))}

                        <div style={{ marginTop: "2rem" }}>
                            <h3 style={{ fontWeight: "bold" }}>Contact Us</h3>
                            <p>If you have any questions or concerns regarding these Terms, please contact us at:</p>
                            <p>
                                <strong>TITA Technologies Inc.</strong>
                                <br />
                                Headquarters - No 81,Rajaram street, jawahar nagar,Thirumangalam-625706
                                <br />
                                Email –{" "}
                                <a href="mailto:contact@titatechnologies.com">
                                    contact@titatechnologies.com
                                </a>
                                <br />
                                Phone: India -{" "}
                                <span style={{ fontWeight: "bold" }}>+91 86672 96757</span>, US/Canada{" "}
                                <span style={{ fontWeight: "bold" }}>+1-647-988-7328</span>
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Privacy Policy Modal */}
            {isPrivacyOpen && (
                <div style={{ display: "flex", position: "fixed", zIndex: "1000", left: "0", top: "0", width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.4)", justifyContent: "center", alignItems: "center" }}>
                    <div style={{ backgroundColor: "#fefefe", padding: "20px", border: "1px solid #888", width: "80%", maxWidth: "80%", height: "90vh", maxHeight: "90vh", overflowY: "auto", scrollBehavior: "smooth", lineHeight: "1.6", borderRadius: "8px", position: "relative" }}>
                        <span style={{ color: "#aaa", position: "absolute", top: "10px", right: "10px", fontSize: "28px", fontWeight: "bold", cursor: "pointer" }} onClick={closePrivacyModal}>&times;</span>
                        <h2>Privacy Policy</h2>
                        {privacySections.map((section, index) => (
                            <div key={index}>
                                <h3 style={{ fontWeight: "bold", marginTop: "20px" }}>{section.title}</h3>
                                <p>{section.description}</p>
                                {Array.isArray(section.content) ? (
                                    <ul style={{ paddingLeft: "1.5rem", listStyleType: "disc" }}>
                                        {section.content.map((item, idx) => (
                                            <li key={idx}><strong>{item.label}:</strong> {item.description}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p>{section.content}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}






            <style jsx>{`
    * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
        font-family: 'Inter', 'Segoe UI', sans-serif;
    }

    body {
        min-height: 100vh;
        display: flex;
        justify-content: center;
        align-items: center;
        background:rgb(255, 255, 255);
        position: relative;
        overflow: hidden; /* Prevent scrolling */
    }

    .form-container {
        position: relative;
        z-index: 2;
        width: 100%;
        height: 80vh; /* Height remains at 80vh */
        max-width: 1000px; /* Increase the container width */
        background: rgba(163, 11, 11, 0.12);
        backdrop-filter: blur(20px);
        border: 1px solid rgb(255, 0, 0);
        border-radius: 24px;
        padding: 1rem; /* Adjusted padding to fit content */
        color: white;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        transform: translateY(20px);
        opacity: 0;
        animation: slideIn 0.6s ease-out forwards;
    }

    @keyframes slideIn {
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }

    .form-header {
        text-align: center;
        margin-bottom: 1rem; /* Reduced margin to fit content */
        font-size: 2rem; /* Reduced font size */
        font-weight: 700;
    }
        label {color: black;}

    .form {
        display: flex;
        flex-direction: column;
        gap: 0.8rem; /* Reduced gap to fit content */
    }

    .form-row {
        display: flex;
        justify-content: space-between;
        gap: 0.4rem; /* Reduced gap to fit content */
    }

    .form-field {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.2rem; /* Reduced gap to fit content */
    }

    .form-field input {
        padding: 0.5rem; /* Reduced padding for input fields */
        border-radius: 6px; /* Reduced border radius */
        border: 1px solid #ccc;
        font-size: 0.9rem; /* Reduced font size for input fields */
        width: 100%;
    }

    .form-gender {
        margin-bottom: 1.5rem;
    }

    .form-gender h3 {
        margin-bottom: 0.5rem;
    }

  .gender-options {
    display: inline-flex;
    gap: 1rem;
    justify-content: flex-start; /* To ensure proper alignment */
}

.gender-options input {
    margin-right: 1rem;
}

    .form-terms {
        margin-top: 1rem;
    }

    .terms-checkbox {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .submit-btn {
        padding: 0.3rem; /* Reduced padding for the submit button */
        width: 40%;
        background: #FF001E;
        color: white;
        border: none;
        border-radius: 10px; /* Reduced border radius */
        font-size: 1rem; /* Reduced font size */
        cursor: pointer;
        transition: 0.3s;
        margin: 0 auto; /* Center the button */
        display: block; /* Ensure it behaves as a block element */
        margin-top:-2px;
        
    }

    .submit-btn:disabled {
        background: #BDC3C7;
        cursor: not-allowed;
        color:#FF001E;
    }

    .error-text {
        color: red;
        font-size: 0.9rem;
    }

    .login-link {
        display: inline-block;
        margin-top: 1rem;
        color: black;
        text-decoration: none;
    }

    .login-link:hover {
        text-decoration: underline;
    }

    .animated-background {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 0;
        overflow: hidden;
    }

    .gradient-sphere {
        position: absolute;
        border-radius: 50%;
        filter: blur(60px);
        opacity: 0.5;
        animation: moveSphere 20s infinite ease-in-out;
    }

    .sphere-1 {
        width: 600px;
        height: 600px;
        background: linear-gradient(45deg, #FF001E, #0A0909);
        top: -300px;
        left: -300px;
        animation-delay: -5s;
    }

    .sphere-2 {
        width: 500px;
        height: 500px;
        background: linear-gradient(45deg, #FF001E, #0A0909);
        bottom: -250px;
        right: -250px;
        animation-delay: -2s;
    }

    .sphere-3 {
        width: 400px;
        height: 400px;
        background: linear-gradient(45deg,rgb(77, 70, 84), #ff00d4);
        top: 50%;
        left: 30%;
        animation-delay: -8s;
    }

    @keyframes moveSphere {
        0%, 100% {
            transform: translate(0, 0) rotate(0deg) scale(1);
        }
        25% {
            transform: translate(100px, 50px) rotate(90deg) scale(1.1);
        }
        50% {
            transform: translate(50px, 100px) rotate(180deg) scale(1);
        }
        75% {
            transform: translate(-50px, 50px) rotate(270deg) scale(0.9);
        }
    }

    .particles {
        position: absolute;
        width: 100%;
        height: 100%;
        z-index: 1;
    }

    .particle {
        position: absolute;
        width: 2px;
        height: 2px;
        background: rgba(255, 255, 255, 0.5);
        border-radius: 50%;
        animation: floatParticle 8s infinite linear;
    }

    @keyframes floatParticle {
        0% {
            transform: translateY(100vh) scale(0);
            opacity: 0;
        }
        50% {
            opacity: 1;
        }
        100% {
            transform: translateY(-100vh) scale(1);
            opacity: 0;
        }
    }
`}</style>

        </>
    );
}

export default SignUpForm;
